'use client';

import * as React from 'react';

import { Badge, type BadgeVariantProps } from '@/hen-ui/atomics/Badge';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { Text } from '@/hen-ui/atomics/Text';
import { NavigationWrapper } from '@/hen-ui/atomics/NavigationWrapper';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Optional metadata pill rendered next to a sidebar row's label. Used
 * for lifecycle tags ("Beta", "New") and similar status indicators —
 * not for primary CTAs. Variant defaults to `secondary` so the badge
 * reads as metadata rather than competing with the active row's fill.
 */
export interface SidebarMenuItemBadge {
  /**
   * Pill text. Trimmed callers should drop the badge when empty rather
   * than passing a whitespace label.
   */
  label: string;
  /**
   * Visual treatment, forwarded to {@link Badge}. Defaults to
   * `secondary`. `primary` is intentionally avoided in default rendering
   * because it competes with the active row's neutral-50 background.
   */
  variant?: BadgeVariantProps['variant'];
}

export interface SidebarMenuItem {
  /**
   * Display label shown in the sidebar row.
   */
  label: string;
  /**
   * Lucide icon name shown to the left of the label.
   * @default 'panel-left'
   */
  icon?: IconName;
  /**
   * Navigation destination for this item.
   */
  href: string;
  /**
   * Whether this item reflects the currently active route.
   * Caller is responsible for computing this (e.g. via `pathname.startsWith`).
   * @default false
   */
  active?: boolean;
  /**
   * Optional badge rendered next to the label (e.g. "Beta", "New"). The
   * badge is also reflected in the mobile toggle row when the item is
   * active, so small viewports still surface the tag without expanding
   * the drawer. Omit to hide.
   */
  badge?: SidebarMenuItemBadge;
}

export interface SidebarMenuProps {
  /**
   * Navigation items to render in the menu.
   * Items are rendered in the order provided.
   */
  items: SidebarMenuItem[];
  /**
   * Used as the accessible `aria-label` for the `<aside>` and `<nav>` landmarks.
   * @default 'Navigation'
   */
  title?: string;
  /**
   * @deprecated No longer rendered. The mobile toggle now shows the active
   * item's icon automatically. Will be removed in the next major release.
   * Kept here so existing consumers don't get a TypeScript error on a patch
   * upgrade.
   */
  titleIcon?: IconName;
  /**
   * @deprecated No longer used. Mobile open/close state is now managed
   * internally. Will be removed in the next major release.
   * Kept here so existing consumers don't get a TypeScript error on a patch
   * upgrade.
   */
  defaultMobileOpen?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SidebarMenu
 *
 * Generic vertical sidebar navigation composed of icon + label rows, each
 * rendered as an anchor via `NavigationWrapper`. Active state is fully
 * prop-driven — the caller computes which item is active (e.g. from the
 * current route) and passes `active: true` on the matching item. Pure UI;
 * no routing logic.
 *
 * Items can optionally carry a `badge` for metadata pills like "Beta" or
 * "New". The badge is rendered next to the label in the expanded list
 * and mirrored into the mobile toggle row for the active item, so small
 * viewports still surface the tag without expanding the drawer.
 *
 * Renders `null` when `items` is empty.
 *
 * @example
 * <SidebarMenu
 *   items={[
 *     { label: 'Overview', href: '/overview', icon: 'layout-dashboard', active: true },
 *     { label: 'Reports',  href: '/reports',  icon: 'bar-chart-2' },
 *     { label: 'AI Assistant', href: '/ai', icon: 'sparkles', badge: { label: 'Beta', variant: 'accent' } },
 *   ]}
 * />
 */
export const SidebarMenu: React.FC<SidebarMenuProps> = ({
  items,
  title = 'Navigation',
  titleIcon: _titleIcon,
  defaultMobileOpen: _defaultMobileOpen,
}) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navId = React.useId();

  if (!items.length) return null;

  const activeItem = items.find((item) => item.active) ?? items[0];
  const activeIconName: IconName = activeItem.icon ?? 'panel-left';

  return (
    <aside
      className='flex w-full flex-col self-start rounded-lg border border-neutral-200 md:max-w-sidebar md:shrink-0'
      aria-label={title}
    >
      {/* ── Mobile toggle button — shows active item, hidden on md+ ── */}
      <button
        type='button'
        className={cn(
          'flex w-full items-center justify-between p-5 md:hidden',
          'cursor-pointer',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
          mobileOpen && 'border-b border-neutral-200',
        )}
        aria-expanded={mobileOpen}
        aria-controls={navId}
        onClick={() => setMobileOpen((prev: boolean) => !prev)}
      >
        <Flex direction='row' align='center' gap='sm'>
          <Icon icon={activeIconName} size='md' color='primary' aria-hidden />
          <Text tag='span' variant='b4' weight='semibold'>
            {activeItem.label}
          </Text>
          {activeItem.badge ? (
            <Badge variant={activeItem.badge.variant ?? 'secondary'} size='sm'>
              {activeItem.badge.label}
            </Badge>
          ) : null}
        </Flex>
        <Icon
          icon={mobileOpen ? 'chevron-up' : 'chevron-down'}
          size='lg'
          color='default'
          aria-hidden
        />
      </button>

      {/* ── Nav list — collapsible on mobile, always visible on md+ ── */}
      <nav
        id={navId}
        aria-label={title}
        className={cn(
          'flex shrink-0 flex-col overflow-hidden rounded-b-lg',
          'md:flex',
          mobileOpen ? 'flex' : 'hidden',
        )}
      >
        {items.map((item, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === items.length - 1;
          const iconName: IconName = item.icon ?? 'panel-left';
          // Only hide the item from the mobile list when it is explicitly active —
          // the toggle button already shows it. When no item is active the toggle
          // falls back to items[0] for display, but items[0] must still be
          // reachable in the expanded drawer.
          const isToggleItem = item.active === true;

          return (
            <NavigationWrapper
              key={item.href}
              href={item.href}
              className={cn(
                'p-5',
                !isFirst && 'border-t border-neutral-100',
                'transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
                item.active
                  ? 'bg-neutral-50'
                  : 'bg-transparent hover:bg-neutral-100',
                isFirst && 'md:rounded-t-lg',
                isLast && 'rounded-b-lg',
                // On mobile this item IS the toggle button — hide it from the list
                isToggleItem && 'hidden md:flex',
              )}
              aria-current={item.active ? 'page' : undefined}
            >
              <Flex direction='row' align='center' gap='sm'>
                <Icon
                  icon={iconName}
                  size='md'
                  color={item.active ? 'primary' : 'default'}
                  aria-hidden
                />
                <Text tag='span' variant='b4' weight='medium'>
                  {item.label}
                </Text>
                {item.badge ? (
                  <Badge variant={item.badge.variant ?? 'secondary'} size='sm'>
                    {item.badge.label}
                  </Badge>
                ) : null}
              </Flex>
            </NavigationWrapper>
          );
        })}
      </nav>
    </aside>
  );
};
